import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import { Toaster } from 'sonner';
import { AppProvider, useApp } from './context/AppContext';
import Login from './pages/Login';
import LandingHero from './pages/LandingHero';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import TrialEnded from './pages/TrialEnded';
import AuthCallback from './pages/AuthCallback';
import Profiles from './pages/Profiles';
import Home from './pages/Home';
import Books from './pages/Books';
import AddBook from './pages/AddBook';
import Reading from './pages/Reading';
import ExercisesScreen from './pages/ExercisesScreen';
import AnswerKeyScreen from './pages/AnswerKeyScreen';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import BookOverview from './pages/BookOverview';
import QuickPractice from './pages/QuickPractice';
const Quiz = lazy(() => import('./pages/Quiz')); // iter-51.21: Beat the Bot
const QuizHistory = lazy(() => import('./pages/QuizHistory')); // iter-51.26
const ReadingHistory = lazy(() => import('./pages/ReadingHistory')); // iter-51.45
const QuizReview = lazy(() => import('./pages/QuizReview')); // iter-51.26
// Iter-51.7 perf: lazy-load the secondary read-once pages. These are
// visited rarely (once per session at most) and were adding ~40 KB to
// the initial bundle every child had to download before the reading
// pane could paint.
const HowToUse = lazy(() => import('./pages/HowToUse'));
const HelpSupport = lazy(() => import('./pages/HelpSupport'));
const UploadGuidelines = lazy(() => import('./pages/UploadGuidelines'));
const DataPrivacy = lazy(() => import('./pages/DataPrivacy'));
const Rewards = lazy(() => import('./pages/Rewards'));
const Billing = lazy(() => import('./pages/Billing'));
// Iter-51.10 — bilingual "Who is this for?" explainer (no audio,
// deliberately, to keep this page fast and lightweight).
const WhoIsThisFor = lazy(() => import('./pages/WhoIsThisFor'));
// Iter-52.16 — dedicated "See all" starter library page reached from
// the Home preview.
const StarterLibrary = lazy(() => import('./pages/StarterLibrary'));
import BottomNav from './components/BottomNav';
import HeaderDebugGate from './components/HeaderDebugGate';
import PlanCapBanner from './components/PlanCapBanner';
import './App.css';

const PageFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/>
  </div>
);

/**
 * ScrollToTop — Iter-51.14 fix. React Router doesn't restore scroll
 * position on route changes by default, so navigating from a deep
 * scroll on the Home feed into a guide page (e.g. HowToUse) left the
 * user staring at the middle of the guide with the language toggle
 * scrolled out of view. Mounted at the top of every Route so every
 * navigation resets to y=0 without each page having to reimplement it.
 */
function ScrollToTop() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'instant' in window ? 'instant' : 'auto' }); }
    catch { window.scrollTo(0, 0); }
  }, [pathname, search]);
  return null;
}

function ProtectedShell({ children, requireChild = true }) {
  const { user, authLoading, activeChild, billing, billingLoading } = useApp();
  const location = useLocation();
  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/></div>;
  if (!user) return <Navigate to="/" state={{ from: location }} replace/>;
  // Iter-49: expired trial → drop the user on the support-contact screen
  // BEFORE any child shell renders. `billingLoading` guards the very
  // first frame after login so we don't flash the shell prematurely.
  if (billing?.status === 'expired') return <Navigate to="/trial-ended" replace/>;
  if (billingLoading && !billing) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/></div>;
  if (requireChild && !activeChild) return <Navigate to="/profiles" replace/>;
  return (
    <div className="min-h-screen pb-24">
      {/* Iter-51.60 — global over-cap warning shown above the header
          so an over-limit account can't miss it. Self-hides when
          under cap or /me/usage errors. */}
      <PlanCapBanner/>
      <div data-testid="app-header" className="w-full flex justify-center pt-4 pb-1">
        {/* Iter-51.15: swapped the plain BrandMark for HeaderDebugGate,
            which wraps it with an app-wide 10-tap gesture. Reachable
            from EVERY authenticated screen (Books, Home, Profile,
            Billing, Reading, empty-state) so the admin panel is no
            longer gated behind having at least one book uploaded. */}
        <HeaderDebugGate/>
      </div>
      {children}
      <BottomNav/>
    </div>
  );
}

/**
 * Iter-52.6 — WelcomeVideoGate removed.
 * The autoplay welcome-video modal was blocking new users from
 * reaching the profile creation form until the ~31-min video ended
 * (they had to hunt for the tiny X). Replaced by an inline greeting
 * inside `Profiles.jsx` that never gates form interaction. The
 * demo video now lives in Profile → Guides & Info as one of the
 * "Important YouTube Links" (Phase-C), so nothing is lost — just
 * moved to a non-blocking spot.
 */

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback/>;
  return (
    <Suspense fallback={<PageFallback/>}>
    <ScrollToTop/>
    <Routes>
      <Route path="/" element={<LandingHeroGate/>}/>
      <Route path="/login" element={<Landing/>}/>
      <Route path="/register" element={<PublicOnly><Register/></PublicOnly>}/>
      <Route path="/verify-email" element={<VerifyEmail/>}/>
      <Route path="/forgot-password" element={<PublicOnly><ForgotPassword/></PublicOnly>}/>
      <Route path="/reset-password" element={<ResetPassword/>}/>
      <Route path="/trial-ended" element={<RequireAuth allowExpired><TrialEnded/></RequireAuth>}/>
      <Route path="/profiles" element={<RequireAuth><Profiles/></RequireAuth>}/>
      <Route path="/home" element={<ProtectedShell><Home/></ProtectedShell>}/>
      <Route path="/books" element={<ProtectedShell><Books/></ProtectedShell>}/>
      <Route path="/books/new" element={<ProtectedShell><AddBook/></ProtectedShell>}/>
      <Route path="/books/:bookId/add-pages" element={<ProtectedShell><AddBook appendMode/></ProtectedShell>}/>
      <Route path="/book/:bookId" element={<ProtectedShell><BookOverview/></ProtectedShell>}/>
      <Route path="/read/:bookId" element={<ProtectedShell><Reading/></ProtectedShell>}/>
      <Route path="/quiz/:bookId" element={<ProtectedShell><Quiz/></ProtectedShell>}/>
      <Route path="/quiz-history" element={<ProtectedShell><QuizHistory/></ProtectedShell>}/>
      <Route path="/reading-history" element={<ProtectedShell><ReadingHistory/></ProtectedShell>}/>
      <Route path="/quiz-review/:runId" element={<ProtectedShell><QuizReview/></ProtectedShell>}/>
      <Route path="/read/:bookId/:pageId" element={<ProtectedShell><Reading/></ProtectedShell>}/>
      <Route path="/exercises/:bookId/:pageId" element={<ProtectedShell><ExercisesScreen/></ProtectedShell>}/>
      <Route path="/exercises/:bookId/:pageId/answers" element={<ProtectedShell><AnswerKeyScreen/></ProtectedShell>}/>
      <Route path="/quick" element={<ProtectedShell><QuickPractice/></ProtectedShell>}/>
      <Route path="/dashboard" element={<ProtectedShell><Dashboard/></ProtectedShell>}/>
      <Route path="/profile" element={<ProtectedShell><Profile/></ProtectedShell>}/>
      <Route path="/billing" element={<ProtectedShell requireChild={false}><Billing/></ProtectedShell>}/>
      <Route path="/how-to-use" element={<ProtectedShell><HowToUse/></ProtectedShell>}/>
      <Route path="/help-support" element={<ProtectedShell><HelpSupport/></ProtectedShell>}/>
      <Route path="/upload-guidelines" element={<ProtectedShell><UploadGuidelines/></ProtectedShell>}/>
      <Route path="/data-privacy" element={<ProtectedShell><DataPrivacy/></ProtectedShell>}/>
      <Route path="/rewards" element={<ProtectedShell><Rewards/></ProtectedShell>}/>
      <Route path="/who-is-this-for" element={<ProtectedShell><WhoIsThisFor/></ProtectedShell>}/>
      <Route path="/library" element={<ProtectedShell><StarterLibrary/></ProtectedShell>}/>
      <Route path="*" element={<Navigate to="/" replace/>}/>
    </Routes>
    </Suspense>
  );
}

function RequireAuth({ children, allowExpired = false }) {
  const { user, authLoading, billing, billingLoading } = useApp();
  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/></div>;
  if (!user) return <Navigate to="/" replace/>;
  // Iter-49: expired parents can only reach /trial-ended and /auth-*
  // pages. `allowExpired` lets the TrialEnded route itself render even
  // once we know the account is out of trial (otherwise we'd infinite-
  // loop the redirect).
  if (!allowExpired && billing?.status === 'expired') return <Navigate to="/trial-ended" replace/>;
  if (!allowExpired && billingLoading && !billing) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/></div>;
  return children;
}

// PublicOnly gates the sign-up / forgot-password screens so a signed-in
// parent doesn't accidentally land there — they get bounced to profiles
// (mirrors the Landing behavior).
function PublicOnly({ children }) {
  const { user, authLoading } = useApp();
  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/></div>;
  if (user) return <Navigate to="/profiles" replace/>;
  return children;
}

function Landing() {
  const { user, authLoading } = useApp();
  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><div className="w-12 h-12 border-4 border-[#FF9F1C] border-t-transparent rounded-full animate-spin"/></div>;
  if (user) return <Navigate to="/profiles" replace/>;
  return <Login/>;
}

// Iter-51.55 — the "/" route now renders the redesigned no-scroll
// mobile landing (LandingHero) instead of the email/password Login
// form. LandingHero itself handles the auth-loading + already-signed-in
// redirect so this shim is trivial. `/login` still uses `Landing`
// (which wraps the existing Login form) for existing users.
function LandingHeroGate() {
  return <LandingHero/>;
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        {/*
          spellCheck={false} at the app root propagates via the HTML `spellcheck`
          attribute inheritance to EVERY descendant element (including <input>
          and <textarea>). This eliminates the native red spellcheck underline
          from lesson content shown in the AddBook page-text editor, book titles,
          reward names, and any other field. Individual inputs additionally
          set autoCorrect="off" / autoCapitalize="off" — those don't inherit.
        */}
        <div spellCheck={false}>
          <AppRouter/>
          {/* Iter-51.24f — bump toast duration so error/success messages
              are readable. Sonner default is 4s which is too short for a
              parent to scan on mobile; 6s + a big close button gives
              them time to react. `closeButton` also lets an impatient
              user dismiss manually. */}
          <Toaster richColors position="top-center" duration={6000} closeButton/>
        </div>
      </BrowserRouter>
    </AppProvider>
  );
}
